<template>
  <div class="vd-root">

    <!-- Header -->
    <div class="vd-header">
      <div>
        <h5 class="vd-title">Visitor Dashboard</h5>
        <p class="vd-sub">Overview of site visits and activity</p>
      </div>
      <div class="vd-header-actions">
        <button class="btn-ghost" @click="load" :disabled="loading">
          <font-awesome-icon :icon="loading ? 'spinner' : 'sync-alt'" :spin="loading" /> Refresh
        </button>
        <router-link :to="$url('/hr/visitors/check-in')" class="btn-primary">
          <font-awesome-icon icon="sign-in-alt" /> Check In Visitor
        </router-link>
      </div>
    </div>

    <!-- Public check-in URL banner -->
    <div v-if="publicCheckInUrl" class="pub-url-bar">
      <div class="pub-url-left">
        <font-awesome-icon icon="link" class="pub-url-icon" />
        <div>
          <div class="pub-url-heading">Public Visitor Check-In Link</div>
          <div class="pub-url-value">{{ publicCheckInUrl }}</div>
        </div>
      </div>
      <button class="btn-copy" :class="{ 'btn-copy--copied': urlCopied }" @click="copyUrl">
        <font-awesome-icon :icon="urlCopied ? 'check' : 'copy'" />
        {{ urlCopied ? 'Copied!' : 'Copy Link' }}
      </button>
    </div>

    <!-- Period filter -->
    <div class="period-bar">
      <div class="period-tabs">
        <button v-for="p in periodOptions" :key="p.value"
          class="period-tab" :class="{ 'period-tab--active': period === p.value }"
          @click="setPeriod(p.value)">
          {{ p.label }}
        </button>
      </div>
      <transition name="fade">
        <div v-if="period === 'custom'" class="period-custom">
          <input type="date" v-model="customFrom" class="period-date" @change="load" />
          <span class="period-sep">to</span>
          <input type="date" v-model="customTo" class="period-date" @change="load" />
        </div>
      </transition>
      <div v-if="!loading && periodLabel" class="period-badge">{{ periodLabel }}</div>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="vd-kpi-row">
        <div v-for="n in 9" :key="n" class="kpi-sk"></div>
      </div>
      <div class="vd-body-sk">
        <div class="sk-block sk-block--wide"></div>
        <div class="sk-block"></div>
        <div class="sk-block"></div>
        <div class="sk-block sk-block--wide"></div>
      </div>
    </template>

    <template v-else>

      <!-- ── KPI cards ──────────────────────────────────────────────────────── -->
      <div class="vd-kpi-row">

        <div class="kpi-card kpi-card--blue">
          <div class="kpi-icon-wrap kpi-icon-wrap--white">
            <font-awesome-icon icon="user-clock" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.active_now }}</div>
            <div class="kpi-label">On Site Now</div>
          </div>
          <router-link :to="$url('/hr/visitors/active')" class="kpi-link">View →</router-link>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrap kpi-icon-wrap--green">
            <font-awesome-icon icon="sign-in-alt" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.total_checkins }}</div>
            <div class="kpi-label">Check-ins</div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrap kpi-icon-wrap--purple">
            <font-awesome-icon icon="sign-out-alt" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.checked_out }}</div>
            <div class="kpi-label">Checked Out</div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrap kpi-icon-wrap--indigo">
            <font-awesome-icon icon="calendar-check" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.pre_registered }}</div>
            <div class="kpi-label">Pre-Registered</div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrap kpi-icon-wrap--amber">
            <font-awesome-icon icon="user-times" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.no_show }}</div>
            <div class="kpi-label">No Shows</div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrap kpi-icon-wrap--red">
            <font-awesome-icon icon="ban" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.denied_at_entry }}</div>
            <div class="kpi-label">Denied at Entry</div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrap kpi-icon-wrap--rose">
            <font-awesome-icon icon="users" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.unique_visitors }}</div>
            <div class="kpi-label">Unique Visitors</div>
          </div>
        </div>

        <div class="kpi-card" :class="{ 'kpi-card--teal': kpi.lockers_in_use > 0 }">
          <div class="kpi-icon-wrap kpi-icon-wrap--teal">
            <font-awesome-icon icon="box" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ kpi.lockers_in_use }}<span class="kpi-of" v-if="kpi.total_lockers"> / {{ kpi.total_lockers }}</span></div>
            <div class="kpi-label">Lockers In Use</div>
          </div>
          <router-link :to="$url('/hr/visitors/lockers')" class="kpi-link-dark">Manage →</router-link>
        </div>

      </div>

      <!-- ── Main grid ─────────────────────────────────────────────────────── -->
      <div class="vd-grid">

        <!-- Visit trend -->
        <div class="vd-card vd-card--wide">
          <div class="vd-card-head">
            <span class="vd-card-title">{{ trendTitle }}</span>
          </div>
          <div class="vd-chart-wrap">
            <GChart
              v-if="trendChartData.length > 1"
              type="AreaChart"
              :data="trendChartData"
              :options="trendOpts"
              style="width:100%;height:240px"
            />
            <div v-else class="vd-chart-empty">
              <font-awesome-icon icon="chart-area" />
              <span>No check-in data for this period</span>
            </div>
          </div>
        </div>

        <!-- Status breakdown -->
        <div class="vd-card">
          <div class="vd-card-head">
            <span class="vd-card-title">Visit Status</span>
          </div>
          <div class="vd-chart-wrap">
            <GChart
              v-if="statusChartData.length > 1"
              type="PieChart"
              :data="statusChartData"
              :options="donutOpts(statusColors)"
              style="width:100%;height:200px"
            />
            <div v-else class="vd-chart-empty">
              <font-awesome-icon icon="chart-pie" />
              <span>No visit data for this period</span>
            </div>
          </div>
          <div class="vd-legend">
            <div v-for="(item, i) in statusLegend" :key="i" class="vd-legend-item">
              <span class="vd-legend-dot" :style="{ background: item.color }"></span>
              <span class="vd-legend-label">{{ item.label }}</span>
              <span class="vd-legend-val">{{ item.count }}</span>
            </div>
          </div>
        </div>

        <!-- Visitor types breakdown -->
        <div class="vd-card">
          <div class="vd-card-head">
            <span class="vd-card-title">Visitor Types</span>
          </div>
          <div class="vd-chart-wrap" v-if="visitorTypes.length">
            <GChart
              type="PieChart"
              :data="typeChartData"
              :options="donutOpts(typeColors)"
              style="width:100%;height:200px"
            />
          </div>
          <div v-else class="vd-chart-empty" style="height:200px;">
            <font-awesome-icon icon="tags" />
            <span>No type data for this period</span>
          </div>
          <div class="vd-legend" v-if="visitorTypes.length">
            <div v-for="(t, i) in visitorTypes" :key="i" class="vd-legend-item">
              <span class="vd-legend-dot" :style="{ background: t.colour }"></span>
              <span class="vd-legend-label">{{ t.name }}</span>
              <span class="vd-legend-val">{{ t.total }}</span>
            </div>
          </div>
        </div>

        <!-- Busiest hours -->
        <div class="vd-card vd-card--wide">
          <div class="vd-card-head">
            <span class="vd-card-title">Busiest Check-in Hours</span>
          </div>
          <div class="vd-chart-wrap">
            <GChart
              v-if="hourlyChartData.length > 1"
              type="ColumnChart"
              :data="hourlyChartData"
              :options="hourlyOpts"
              style="width:100%;height:200px"
            />
            <div v-else class="vd-chart-empty">
              <font-awesome-icon icon="chart-bar" />
              <span>No data for this period</span>
            </div>
          </div>
        </div>

        <!-- Top hosts -->
        <div class="vd-card">
          <div class="vd-card-head">
            <span class="vd-card-title">Top Hosts</span>
          </div>
          <div v-if="topHosts.length" class="vd-host-list">
            <div v-for="(h, i) in topHosts" :key="i" class="vd-host-row">
              <div class="vd-host-rank">{{ i + 1 }}</div>
              <div class="vd-host-avatar">{{ h.initials }}</div>
              <div class="vd-host-info">
                <div class="vd-host-name">{{ h.name }}</div>
                <div class="vd-host-bar-wrap">
                  <div class="vd-host-bar" :style="{ width: hostBarPct(h.visit_count) }"></div>
                </div>
              </div>
              <div class="vd-host-count">
                <span class="vd-host-badge">{{ h.visit_count }}</span>
              </div>
            </div>
          </div>
          <div v-else class="vd-chart-empty">
            <font-awesome-icon icon="users" />
            <span>No host data for this period</span>
          </div>
        </div>

        <!-- Recent activity -->
        <div class="vd-card vd-card--full">
          <div class="vd-card-head">
            <span class="vd-card-title">Recent Activity</span>
            <router-link :to="$url('/hr/visitors/history')" class="vd-card-link">View all →</router-link>
          </div>
          <div v-if="recentActivity.length" class="vd-activity-list">
            <div v-for="v in recentActivity" :key="v.id" class="vd-activity-row">
              <div class="vd-act-avatar">{{ v.visitor_initials }}</div>
              <div class="vd-act-info">
                <div class="vd-act-name">
                  {{ v.visitor_name }}
                  <span v-if="v.visitor_company" class="vd-act-company">· {{ v.visitor_company }}</span>
                </div>
                <div class="vd-act-meta">
                  Visiting <strong>{{ v.host_name }}</strong>
                  <span v-if="v.purpose"> · {{ v.purpose }}</span>
                </div>
              </div>
              <div class="vd-act-right">
                <span v-if="v.visitor_type" class="vd-type-chip" :style="typeChipStyle(v.visitor_type)">{{ v.visitor_type.name }}</span>
                <span class="vd-status-chip" :class="statusClass(v.status)">{{ fmtStatus(v.status) }}</span>
                <div class="vd-act-time">{{ fmtRelative(v.checked_in_at) }}</div>
              </div>
            </div>
          </div>
          <div v-else class="vd-chart-empty" style="padding:32px 0;">
            <font-awesome-icon icon="history" />
            <span>No recent activity</span>
          </div>
        </div>

      </div>
    </template>

  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

const STATUS_COLORS = {
  checked_in:     '#004AAD',
  checked_out:    '#16a34a',
  pre_registered: '#7c3aed',
  no_show:        '#ca8a04',
  cancelled:      '#dc2626',
  denied_at_entry:'#be185d',
  approved:       '#0d9488',
  auto_closed:    '#64748b',
}
const STATUS_LABELS = {
  checked_in:     'Checked In',
  checked_out:    'Checked Out',
  pre_registered: 'Pre-Registered',
  no_show:        'No Show',
  cancelled:      'Cancelled',
  denied_at_entry:'Denied at Entry',
  approved:       'Approved',
  auto_closed:    'Auto-Closed',
}

export default {
  name: 'VisitorDashboard',
  setup() { return { toast: useToast() } },

  data() {
    return {
      loading: true,
      urlCopied: false,
      businessId: null,
      period: 'month',
      customFrom: '',
      customTo: '',
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: 'week',  label: 'This Week' },
        { value: 'month', label: 'This Month' },
        { value: 'year',  label: 'This Year' },
        { value: 'custom',label: 'Custom' },
      ],
      periodFrom: null,
      periodTo: null,
      kpi: { active_now:0, total_checkins:0, checked_out:0, pre_registered:0, no_show:0, denied_at_entry:0, unique_visitors:0, lockers_in_use:0, total_lockers:0 },
      trend:           [],
      hourly:          [],
      statusBreakdown: {},
      visitorTypes:    [],
      topHosts:        [],
      recentActivity:  [],
    }
  },

  computed: {
    publicCheckInUrl() {
      const code  = this.$route.params.companyCode
      const bizId = this.businessId || this.$store.state.getLoggedInUser?.business_id
      if (!code || !bizId) return null
      return `${window.location.origin}/visitor-checkin/${code}/${bizId}`
    },

    periodLabel() {
      if (!this.periodFrom || !this.periodTo) return null
      const fmt = d => new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
      if (this.period === 'today') return fmt(this.periodFrom)
      return `${fmt(this.periodFrom)} – ${fmt(this.periodTo)}`
    },

    trendTitle() {
      if (this.period === 'today') return 'Check-ins by Hour (Today)'
      if (this.period === 'year')  return 'Monthly Check-ins (This Year)'
      return 'Daily Check-ins'
    },

    trendChartData() {
      const rows = [['Period', 'Visitors']]
      this.trend.forEach(r => rows.push([r.label, r.total]))
      return rows
    },
    trendOpts() {
      return {
        legend: { position: 'none' },
        colors: ['#004AAD'],
        areaOpacity: 0.1,
        chartArea: { left: 44, right: 10, top: 10, bottom: 40, width: '100%', height: '80%' },
        hAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, gridlines: { color: 'transparent' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, gridlines: { color: '#f0f4f8' }, minValue: 0, format: '#' },
        backgroundColor: 'transparent',
        lineWidth: 2,
        pointSize: 4,
      }
    },

    statusChartData() {
      const rows = [['Status', 'Count']]
      Object.entries(this.statusBreakdown).forEach(([k, v]) => rows.push([STATUS_LABELS[k] ?? k, Number(v)]))
      return rows
    },
    statusColors() {
      return Object.keys(this.statusBreakdown).map(k => STATUS_COLORS[k] ?? '#aab8cc')
    },
    statusLegend() {
      return Object.entries(this.statusBreakdown).map(([k, v]) => ({
        label: STATUS_LABELS[k] ?? k,
        color: STATUS_COLORS[k] ?? '#aab8cc',
        count: Number(v),
      }))
    },

    typeChartData() {
      const rows = [['Type', 'Count']]
      this.visitorTypes.forEach(t => rows.push([t.name, Number(t.total)]))
      return rows
    },
    typeColors() {
      return this.visitorTypes.map(t => t.colour || '#aab8cc')
    },

    hourlyChartData() {
      const rows = [['Hour', 'Visitors']]
      this.hourly.forEach(r => rows.push([r.label, r.total]))
      return rows
    },
    hourlyOpts() {
      return {
        legend: { position: 'none' },
        colors: ['#004AAD'],
        chartArea: { left: 44, right: 10, top: 10, bottom: 40, width: '100%', height: '75%' },
        hAxis: { textStyle: { fontSize: 10, color: '#7a90b0' }, gridlines: { color: 'transparent' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, gridlines: { color: '#f0f4f8' }, minValue: 0, format: '#' },
        backgroundColor: 'transparent',
        bar: { groupWidth: '70%' },
      }
    },

    maxHostCount() {
      return Math.max(...this.topHosts.map(h => h.visit_count), 1)
    },
  },

  created() { this.load() },

  methods: {
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.publicCheckInUrl)
        this.urlCopied = true
        setTimeout(() => { this.urlCopied = false }, 2500)
      } catch { this.urlCopied = false }
    },

    setPeriod(p) {
      this.period = p
      if (p !== 'custom') this.load()
    },

    donutOpts(colors) {
      return {
        legend: { position: 'none' },
        pieHole: 0.52,
        colors,
        chartArea: { left: 10, right: 10, top: 10, bottom: 10, width: '100%', height: '100%' },
        backgroundColor: 'transparent',
        pieSliceBorderColor: '#fff',
        tooltip: { trigger: 'selection' },
      }
    },

    async load() {
      this.loading = true
      try {
        const params = { period: this.period }
        if (this.period === 'custom') {
          if (this.customFrom) params.from = this.customFrom
          if (this.customTo)   params.to   = this.customTo
        }
        const r = await axios.get('visitors/dashboard', { params })
        const d = r.data.data
        if (d.business_id) this.businessId = d.business_id
        this.periodFrom      = d.period_from
        this.periodTo        = d.period_to
        this.kpi             = d.kpi
        this.trend           = d.trend
        this.hourly          = d.hourly
        this.statusBreakdown = d.status_breakdown
        this.visitorTypes    = d.visitor_types
        this.topHosts        = d.top_hosts
        this.recentActivity  = d.recent_activity
      } catch { this.toast.error('Failed to load dashboard.') }
      finally { this.loading = false }
    },

    hostBarPct(count) {
      return `${Math.round((count / this.maxHostCount) * 100)}%`
    },

    statusClass(s) {
      return {
        checked_in:      'chip--blue',
        checked_out:     'chip--green',
        pre_registered:  'chip--purple',
        no_show:         'chip--amber',
        cancelled:       'chip--red',
        denied_at_entry: 'chip--pink',
        auto_closed:     'chip--gray',
      }[s] ?? 'chip--gray'
    },

    fmtStatus(s) { return STATUS_LABELS[s] ?? s },

    typeChipStyle(vt) {
      if (!vt?.colour) return {}
      return { background: vt.colour + '22', color: vt.colour, border: `1px solid ${vt.colour}44` }
    },

    fmtRelative(iso) {
      if (!iso) return '—'
      const diff = Math.floor((Date.now() - new Date(iso)) / 1000)
      if (diff < 60)    return 'just now'
      if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    },
  },
}
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────────────── */
.vd-root { display:flex; flex-direction:column; gap:20px; }

.vd-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; }
.vd-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.vd-sub    { font-size:13px; color:#7a90b0; margin:0; }
.vd-header-actions { display:flex; gap:8px; align-items:center; }

.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-primary:hover { background:#003b8e; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-ghost:disabled { opacity:.5; cursor:not-allowed; }

/* ── Public URL banner ───────────────────────────────────────────────────────── */
.pub-url-bar { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:14px 18px; }
.pub-url-left { display:flex; align-items:center; gap:12px; min-width:0; }
.pub-url-icon { color:#004AAD; font-size:14px; flex-shrink:0; }
.pub-url-heading { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:2px; }
.pub-url-value { font-size:13px; font-weight:600; color:#1a2b45; word-break:break-all; }
.btn-copy { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:12px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; cursor:pointer; font-family:inherit; white-space:nowrap; flex-shrink:0; transition:background .15s,color .15s; }
.btn-copy:hover { background:#e4eaf2; }
.btn-copy--copied { background:#dcfce7; color:#16a34a; border-color:#bbf7d0; }

/* ── Period filter bar ───────────────────────────────────────────────────────── */
.period-bar { display:flex; align-items:center; gap:12px; flex-wrap:wrap; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:12px 16px; }
.period-tabs { display:flex; gap:4px; background:#f4f7fb; border-radius:8px; padding:3px; }
.period-tab { padding:6px 14px; border:none; border-radius:6px; font-size:13px; font-weight:500; color:#7a90b0; background:none; cursor:pointer; font-family:inherit; transition:all .15s; white-space:nowrap; }
.period-tab--active { background:#fff; color:#004AAD; font-weight:700; box-shadow:0 1px 4px rgba(0,0,0,.1); }
.period-tab:hover:not(.period-tab--active) { color:#3d5068; }
.period-custom { display:flex; align-items:center; gap:8px; }
.period-date { padding:6px 10px; border:1px solid #dde5f0; border-radius:7px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; }
.period-date:focus { border-color:#004AAD; }
.period-sep { font-size:12px; color:#aab8cc; }
.period-badge { margin-left:auto; font-size:11.5px; color:#7a90b0; background:#f4f7fb; border:1px solid #e4eaf2; padding:4px 12px; border-radius:20px; white-space:nowrap; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s; }
.fade-enter-from, .fade-leave-to { opacity:0; }

/* ── KPI cards ───────────────────────────────────────────────────────────────── */
.vd-kpi-row { display:grid; grid-template-columns:repeat(auto-fill, minmax(170px, 1fr)); gap:14px; }

.kpi-card {
  background:#fff; border:1px solid #e4eaf2; border-radius:14px;
  padding:18px 16px; display:flex; flex-direction:column; gap:10px;
  position:relative; overflow:hidden; transition:box-shadow .15s;
}
.kpi-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.07); }
.kpi-card--blue { background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%); border-color:transparent; }
.kpi-card--blue .kpi-value { color:#fff; }
.kpi-card--blue .kpi-label { color:rgba(255,255,255,.75); }
.kpi-card--teal { border-color:#99f6e4; }

.kpi-icon-wrap { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.kpi-icon-wrap--white  { background:rgba(255,255,255,.2); color:#fff; }
.kpi-icon-wrap--green  { background:#dcfce7; color:#16a34a; }
.kpi-icon-wrap--purple { background:#ede9fe; color:#7c3aed; }
.kpi-icon-wrap--indigo { background:#e0e7ff; color:#4338ca; }
.kpi-icon-wrap--amber  { background:#fef9c3; color:#ca8a04; }
.kpi-icon-wrap--red    { background:#fee2e2; color:#dc2626; }
.kpi-icon-wrap--rose   { background:#ffe4e6; color:#e11d48; }
.kpi-icon-wrap--teal   { background:#ccfbf1; color:#0d9488; }

.kpi-body  { flex:1; }
.kpi-value { font-size:26px; font-weight:800; color:#1a2b45; line-height:1.1; }
.kpi-of    { font-size:14px; font-weight:500; color:#7a90b0; }
.kpi-label { font-size:12px; color:#7a90b0; margin-top:2px; font-weight:500; }
.kpi-link  { font-size:11px; font-weight:700; color:rgba(255,255,255,.8); text-decoration:none; }
.kpi-link:hover { color:#fff; }
.kpi-link-dark { font-size:11px; font-weight:700; color:#7a90b0; text-decoration:none; }
.kpi-link-dark:hover { color:#004AAD; }

/* ── Main grid ───────────────────────────────────────────────────────────────── */
.vd-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.vd-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; }
.vd-card--wide { grid-column:span 2; }
.vd-card--full { grid-column:1 / -1; }

.vd-card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.vd-card-title { font-size:14px; font-weight:700; color:#1a2b45; }
.vd-card-link  { font-size:12px; font-weight:600; color:#004AAD; text-decoration:none; }
.vd-card-link:hover { text-decoration:underline; }

.vd-chart-wrap  { width:100%; }
.vd-chart-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; height:200px; color:#c0cad6; font-size:13px; }
.vd-chart-empty svg { font-size:28px; }

/* ── Legend ─────────────────────────────────────────────────────────────────── */
.vd-legend { display:flex; flex-direction:column; gap:6px; margin-top:12px; }
.vd-legend-item { display:flex; align-items:center; gap:8px; font-size:12px; }
.vd-legend-dot   { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.vd-legend-label { flex:1; color:#3d5068; }
.vd-legend-val   { font-weight:700; color:#1a2b45; }

/* ── Top hosts ───────────────────────────────────────────────────────────────── */
.vd-host-list { display:flex; flex-direction:column; gap:12px; }
.vd-host-row  { display:flex; align-items:center; gap:10px; }
.vd-host-rank { width:18px; font-size:11px; font-weight:800; color:#c0cad6; text-align:center; flex-shrink:0; }
.vd-host-avatar { width:34px; height:34px; border-radius:50%; background:#e0e7ff; color:#4338ca; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.vd-host-info  { flex:1; min-width:0; }
.vd-host-name  { font-size:13px; font-weight:600; color:#1a2b45; margin-bottom:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.vd-host-bar-wrap { height:4px; background:#f0f4f8; border-radius:2px; }
.vd-host-bar   { height:4px; background:linear-gradient(90deg,#004AAD,#1565d8); border-radius:2px; transition:width .4s ease; }
.vd-host-count { flex-shrink:0; }
.vd-host-badge { background:#e0e7ff; color:#4338ca; font-size:11px; font-weight:700; padding:2px 8px; border-radius:10px; }

/* ── Activity feed ───────────────────────────────────────────────────────────── */
.vd-activity-list { display:flex; flex-direction:column; }
.vd-activity-row  { display:flex; align-items:center; gap:12px; padding:12px 0; border-bottom:1px solid #f0f4f8; }
.vd-activity-row:last-child { border-bottom:none; }
.vd-act-avatar { width:38px; height:38px; border-radius:50%; background:#e0e7ff; color:#4338ca; font-size:13px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.vd-act-info   { flex:1; min-width:0; }
.vd-act-name   { font-size:13px; font-weight:600; color:#1a2b45; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.vd-act-company { font-weight:400; color:#7a90b0; }
.vd-act-meta   { font-size:12px; color:#7a90b0; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.vd-act-right  { display:flex; flex-direction:column; align-items:flex-end; gap:4px; flex-shrink:0; }
.vd-act-time   { font-size:11px; color:#aab8cc; }

.vd-type-chip { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:600; white-space:nowrap; }

/* ── Status chips ────────────────────────────────────────────────────────────── */
.vd-status-chip { display:inline-flex; align-items:center; padding:2px 9px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--pink   { background:#fce7f3; color:#be185d; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

/* ── Skeletons ────────────────────────────────────────────────────────────────── */
.kpi-sk { height:100px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:14px; }
.vd-body-sk { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.sk-block { height:280px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:14px; }
.sk-block--wide { grid-column:span 2; height:300px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Responsive ─────────────────────────────────────────────────────────────── */
@media (max-width:900px) {
  .vd-grid { grid-template-columns:1fr; }
  .vd-card--wide, .vd-card--full { grid-column:span 1; }
  .period-bar { flex-direction:column; align-items:flex-start; }
}
@media (max-width:600px) {
  .vd-kpi-row { grid-template-columns:repeat(2,1fr); }
  .period-tabs { flex-wrap:wrap; }
}
</style>
